import React from 'react';
import { Alert, Typography, Box } from '@mui/material';
import { CheckCircle, Error, Warning } from '@mui/icons-material';
import { AddressValidationResult } from './AddressValidationInput';

interface ValidationFeedbackProps {
  result: AddressValidationResult;
  showDetails?: boolean;
}

const ValidationFeedback: React.FC<ValidationFeedbackProps> = ({ 
  result, 
  showDetails = true 
}) => {
  if (result.success && result.validated && result.address) {
    return (
      <Alert 
        severity="success" 
        icon={<CheckCircle />}
        sx={{ '& .MuiAlert-message': { width: '100%' } }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            Address validated successfully
          </Typography>
          {showDetails && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                {result.address.formattedAddress}
              </Typography>
              {result.address.confidence && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  Confidence: {result.address.confidence}
                  {result.metadata?.cached && ' (cached)'}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Alert>
    );
  }

  if (!result.success && result.error) {
    const severity = result.error.retryable ? 'warning' : 'error';
    const icon = result.error.retryable ? <Warning /> : <Error />;
    
    return (
      <Alert 
        severity={severity} 
        icon={icon}
        sx={{ '& .MuiAlert-message': { width: '100%' } }}
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {result.error.message}
          </Typography>
          {showDetails && result.error.retryable && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              You can try again or continue with the unvalidated address.
            </Typography>
          )}
          {showDetails && result.error.code && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
              Error code: {result.error.code}
            </Typography>
          )}
        </Box>
      </Alert>
    );
  }

  // Fallback for unexpected states
  return (
    <Alert severity="info">
      <Typography variant="body2">
        Address validation status unknown
      </Typography>
    </Alert>
  );
};

export default ValidationFeedback;
